import React, { useEffect, useRef, useState } from "react";
import { Modal } from "Components/Modal";
import styled from "styled-components";
import { yupNumber, yupOptional } from "Utils/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { Switch } from "Components/Switch";
import { TextInput } from "melodies-source/TextInput";
import { Select } from "melodies-source/Select";
import { useFormik } from "formik";
import { Field, FieldKey, PartnerOptInsValue } from "..";
import { Body1, Label, Td } from "melodies-source/Text";
import { deleteField, setDoc } from "firebase/firestore";
import { FlexRow } from "Components/Flex";
import { SvgCheck } from "melodies-source/Svgs/Check";

const spotifyIdRegex = /\w{22}/;

const INITIAL_VALUES = {
  brandPartnerships: false,
  groupType: "",
  maxArtistId: "",
  partnerOptIns: { umg: false, wmg: false },
  postShowEmail: "",
  sortOrder: "",
  spotifyArtistId: "",
};

const VALIDATION_SCHEMA = yup.object().shape({
  maxArtistId: yupNumber.optional(),
  spotifyArtistId: yupOptional.matches(spotifyIdRegex, "Invalid ID"),
  sortOrder: yupNumber
    .optional()
    .test("is-less-than-max-safe-integer", "Invalid sort order", (value) => {
      if (typeof value === "string" || typeof value === "number") {
        return +value < Number.MAX_SAFE_INTEGER - 1;
      }
      return true;
    }),
  postShowEmail: yupOptional,
  brandPartnerships: yup.boolean(),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  fields: Field[];
}

export const EditFieldsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  title,
  fields,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: VALIDATION_SCHEMA,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: handleSave,
  });

  async function handleSave() {
    try {
      setLoading(true);

      const data: Record<
        FieldKey,
        string | number | boolean | Record<string, boolean>
      > = {
        ...formik.values,
      };

      if (formik.values.maxArtistId) {
        data.maxArtistId = +formik.values.maxArtistId;
      }

      if (formik.values.spotifyArtistId) {
        const match = spotifyIdRegex.exec(formik.values.spotifyArtistId);
        data.spotifyArtistId = match?.[0] || formik.values.spotifyArtistId;
      }

      formik.setFieldValue("spotifyArtistId", data.spotifyArtistId);

      if (formik.values.sortOrder) {
        data.sortOrder = +formik.values.sortOrder;
      }

      const updateFieldPromises = fields
        .map(async (field) => {
          if (field.component === "partner-opt-ins") {
            return Promise.all(
              Object.entries(data.partnerOptIns).map(([key, value]) => {
                const option = field.options.find((opt) => opt.value === key);
                if (!option?.ref) {
                  throw new Error(`Invalid option reference for ${key}`);
                }
                return setDoc(
                  option.ref,
                  { uiVisible: value },
                  { merge: true },
                );
              }),
            );
          }
          // escape hatch to handle `empty`/`emptied` sortOrder fields
          if (field.key === "sortOrder") {
            return setDoc(
              field.ref,
              {
                sortOrder: data.sortOrder ? data.sortOrder : deleteField(),
              },
              { merge: true },
            );
          }

          if (field.key === "brandPartnerships") {
            if (data.brandPartnerships) {
              return setDoc(field.ref, {}, { merge: true });
            } else {
              return null;
            }
          }

          return setDoc(
            field.ref,
            {
              [field.key]:
                data[field.key] === "" ? deleteField() : data[field.key],
            },
            { merge: true },
          );
        })
        .filter(Boolean);

      await Promise.all(updateFieldPromises);
      toast.success("Admin access fields saved successfully");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("There was an error saving the fields. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const getInputProps = (fieldKey: string) => ({
    value: formik.values[fieldKey],
    onChange: formik.handleChange(fieldKey),
    ...(formik.errors[fieldKey] && {
      hasError: true,
      helperText: formik.errors[fieldKey],
    }),
  });

  useEffect(() => {
    if (isOpen) {
      for (const field of fields) {
        if (field.value) {
          formik.setFieldValue(field.key, field.value);
        }
      }
    }
  }, [JSON.stringify([fields.map((field) => field.value)]), isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={title}
      actions={{
        main: {
          text: "Save",
          onClick: () => buttonRef.current?.click(),
          loading: loading,
        },
        secondary: {
          text: "Cancel",
          onClick: onClose,
        },
      }}
      withBackdropClose={false}
    >
      <Body1>If you don't have an ID, you can leave the field blank.</Body1>
      <Form onSubmit={formik.handleSubmit}>
        {fields.map((field) => {
          switch (field.component) {
            case "text-input": {
              return (
                <TextInput
                  key={field.key}
                  label={field.label}
                  name={field.key}
                  placeholder={field.placeholder}
                  type={field.type}
                  {...getInputProps(field.key)}
                />
              );
            }
            case "select": {
              return (
                <Select
                  key={field.key}
                  label={field.label}
                  options={field.options}
                  {...getInputProps(field.key)}
                />
              );
            }
            case "partner-opt-ins": {
              return (
                <FieldColumn key={field.key}>
                  <Label>{field.label}</Label>
                  {field.options.map((opt) => (
                    <SwitchContainer key={opt.value}>
                      <Switch
                        value={formik.values[field.key][opt.value]}
                        onChange={(value) =>
                          formik.setFieldValue(field.key, {
                            ...(formik.values[field.key] as PartnerOptInsValue),
                            [opt.value]: value,
                          })
                        }
                      />
                      <Body1>{opt.label}</Body1>
                    </SwitchContainer>
                  ))}
                </FieldColumn>
              );
            }
            case "launch":
              if (field.value === true) {
                return (
                  <FieldColumn key={field.key}>
                    <Label>{field.label}</Label>
                    <FlexRow gap="8px">
                      <SvgCheck />
                      <Td>Enabled</Td>
                    </FlexRow>
                  </FieldColumn>
                );
              }
              return (
                <FieldColumn key={field.key}>
                  <Label>{field.label}</Label>
                  <SwitchContainer>
                    <Switch
                      value={formik.values[field.key]}
                      onChange={(value) =>
                        formik.setFieldValue(field.key, value)
                      }
                      isDisabled={field.disabled}
                    />
                    {typeof field.text === "string" ? (
                      <Body1>{field.text}</Body1>
                    ) : (
                      field.text
                    )}
                  </SwitchContainer>
                </FieldColumn>
              );
            default:
              return null;
          }
        })}
        <button ref={buttonRef} type="submit" hidden />
      </Form>
    </Modal>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const FieldColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0;

  ${Label} {
    color: var(--text-color);
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${Body1} {
    font-size: 14px;
    font-weight: 500;
  }
`;
